import { TYPO } from "styles/Typo";
import DiagnosisLayout from "./Layout";
import { useSharpeData } from "utils/clientAPIs/detailDiagnosis";
import { useParams } from "next/navigation";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import SkeletonBlock from "components/skeleton";
import moment from "moment-timezone";
import { TrackedLink } from "lib/analytics/react/link";
function DetailDiagnosisSharpe({
  score,
  base_dt,
  detailData,
}: {
  score?: number;
  base_dt?: string;
  detailData: any;
}) {
  const { ticker } = useParams();
  const { t } = useTranslation("detail");
  const Headers = [
    {
      title: t("risk"),
      key: "EXP_CVaRNTS_95",
    },
    {
      title: t("1YearReturn"),
      key: "ADJ_CHG_1Y",
    },
    {
      title: t("returnVsRisk"),
      key: "RAR",
    },
  ];
  const router = useRouter();
  const [sharpeData, isSharpeLoading] = useSharpeData(
    ticker as string,
    detailData?.ITEM_CD,
    detailData?.ITEM_CD !== undefined,
    true
  );
  const Text = ({
    text,
    index,
    className,
  }: {
    text: string;
    index: number;
    className?: string;
  }) => {
    return (
      <p
        key={index}
        className={`${TYPO.TYPO7_1_RG} text-gray80 flex-1 grow ${className}`}
        style={{
          textAlign:
            index === Headers.length - 1
              ? "right"
              : index === 0
              ? "left"
              : "center",
        }}
      >
        {text}
      </p>
    );
  };

  const loading =
    (!score && String(score) !== "0") || isSharpeLoading || !detailData;
  const riskMedain = sharpeData?.riskMedain;
  const standardRaR = sharpeData?.standardRaR;
  const RAR = sharpeData?.RAR;
  const EXP_CVaRNTS_95 = sharpeData?.EXP_CVaRNTS_95;
  const ADJ_CHG_1Y = sharpeData?.ADJ_CHG_1Y;
  const desc =
    ADJ_CHG_1Y > 0
      ? Math.abs(riskMedain) > Math.abs(EXP_CVaRNTS_95)
        ? t("sharpeDescLowRiskGain")
        : t("sharpeDescHighRiskGain")
      : Math.abs(riskMedain) > Math.abs(EXP_CVaRNTS_95)
      ? t("sharpeDescLowRiskLoss")
      : t("sharpeDescHighRiskLoss");
  const lastUpdateDate =
    router.locale === "ko"
      ? moment(base_dt).format("M월 D일")
      : moment(base_dt).format("MMM D");
  if (loading) {
    return (
      <div className="px-6 pt-[30px] pb-1 gap-1 flex flex-col">
        <SkeletonBlock width={"100%"} height={289} radius={10} />
      </div>
    );
  }
  return (
    <div className="px-6 pt-[30px] pb-1 gap-1 flex flex-col">
      <DiagnosisLayout>
        <DiagnosisLayout.Header score={score}>
          <DiagnosisLayout.Header.Title>
            {t("returnPerformance")}
          </DiagnosisLayout.Header.Title>
          <DiagnosisLayout.Header.SubTitle>
            {desc}
          </DiagnosisLayout.Header.SubTitle>
        </DiagnosisLayout.Header>
        <DiagnosisLayout.ContentWrapper height={90}>
          <DiagnosisLayout.Content>
            {Headers.map((header, index) => (
              <p
                key={index}
                className={`${TYPO.TYPO7_1_RG} !text-gray50 flex-1 grow whitespace-nowrap`}
                style={{
                  textAlign:
                    index === Headers.length - 1
                      ? "right"
                      : index === 0
                      ? "left"
                      : "center",
                }}
              >
                {header.title}
              </p>
            ))}
          </DiagnosisLayout.Content>

          <DiagnosisLayout.Content>
            {Headers.slice(0, 2).map((header, index) => {
              let value = sharpeData?.[header.key];
              const isNumber = typeof value === "number";
              if (isNumber) {
                value = value.toFixed(1);
              }
              if (header.key === "EXP_CVaRNTS_95") {
                value =
                  Math.abs(value) > sharpeData?.riskMedain
                    ? t("high")
                    : t("low");
              }
              return (
                <Text
                  text={header?.key === "ADJ_CHG_1Y" ? value + "%" : value}
                  index={index}
                  key={index}
                  className={
                    header?.key === "ADJ_CHG_1Y"
                      ? value === "0.0"
                        ? "!text-gray40"
                        : Number(value) > 0
                        ? "!text-red40"
                        : "!text-blue40"
                      : ""
                  }
                />
              );
            })}
            <div
              className="flex grow flex-1 justify-end items-center"
              key={Headers.length}
            >
              <p
                className={`${TYPO.TYPO7_1_MD} bg-gray5 rounded-[4px] px-2 w-fit text-gray80 `}
                style={{
                  textAlign: "right",
                }}
              >
                {ADJ_CHG_1Y > 0
                  ? RAR > standardRaR
                    ? t("high")
                    : t("normal")
                  : t("low")}
              </p>
            </div>
          </DiagnosisLayout.Content>
        </DiagnosisLayout.ContentWrapper>
        <TrackedLink
          href={`/detail/${ticker}/sharpe`}
          itemspec={{
            kind: "section_nav",
            section_id: "stock_attractiveness_sharpe",
            nav_key: "stock_navbtn_sharpe_detail",
          }}
          asTrace={true}
        >
          <DiagnosisLayout.ViewMore
            onClick={() => {
              router.push(`/detail/${ticker}/sharpe`);
            }}
            type="sharpe"
          />
        </TrackedLink>
      </DiagnosisLayout>
      <div className="flex justify-end">
        <p className={`${TYPO.TYPO8_RG} text-gray40`}>
          {t("lastUpdated", { date: lastUpdateDate })}
        </p>
      </div>
    </div>
  );
}

export default DetailDiagnosisSharpe;
